import { ForwardedRef, forwardRef, ReactNode } from 'react';
import { css } from '@emotion/css';
import { Row, Col, Typography } from '@prezzee/components';
import { spacing, colors } from '@prezzee/tokens';

const style = {
  container: css`
    border: 1px solid ${colors.gray300};
    border-radius: 4px; // Should come from tokens
    width: 100%;
    margin-bottom: ${spacing.spacing04};
  `,
  content: css`
    padding: 0 ${spacing.spacing05};

    @media (max-width: 1288px) {
      padding: 0 ${spacing.spacing04};
    }
  `,
  toggle: css`
    position: relative;
    padding: ${spacing.spacing05} calc(${spacing.spacing05} + ${spacing.spacing10}) ${spacing.spacing05}
      ${spacing.spacing05};
    text-align: left;
    width: 100%;

    @media (max-width: 1288px) {
      padding: ${spacing.spacing05} calc(${spacing.spacing04} + ${spacing.spacing10}) ${spacing.spacing05}
        ${spacing.spacing04};
    }

    &:active {
      outline: none;
    }

    &:disabled {
      pointer-events: none;
    }
  `,
  title: (disabled: boolean) => css`
    color: ${disabled ? `${colors.gray500} !important` : 'inherit'};
    margin-right: ${spacing.spacing04};
  `,
  subtitle: css`
    color: ${colors.gray500};
    line-height: 1.8em !important;
  `,
  toggleActionText: css`
    position: absolute;
    right: ${spacing.spacing05};
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.notificationBlue} !important;

    @media (max-width: 576px) {
      margin-top: 2px !important; //Better aligns the text on mobile
    }

    @media (max-width: 1288px) {
      right: ${spacing.spacing04};
    } ;
  `,
  text: css`
    display: flex;
    align-items: baseline;
  `,
};

interface AccordionStepProps {
  children?: ReactNode;
  title?: string;
  subtitle?: string;
  stepIndex?: number;
  currentStep?: number;
  furthestStep?: number;
  disabled?: boolean;
  hidden?: boolean;
  onToggle?: () => void;
}

const AccordionStep = forwardRef(
  (
    {
      children,
      title,
      subtitle,
      stepIndex = 0,
      currentStep = 0,
      furthestStep = 0,
      disabled = false,
      hidden = false,
      onToggle,
    }: AccordionStepProps,
    ref: ForwardedRef<HTMLElement>
  ): JSX.Element => {
    const handleToggleClick = () => {
      onToggle && onToggle();
    };

    const expanded = !disabled && !hidden;
    const ariaTitle = 'step-' + title.toLowerCase().split(' ').join('-');
    const isActive = currentStep === stepIndex;

    return (
      <section className={style.container} ref={ref}>
        <h3 className="sr-only">
          {title}
          {hidden && subtitle}
        </h3>
        <button
          className={style.toggle}
          onClick={handleToggleClick}
          disabled={disabled || !hidden}
          aria-controls={`${ariaTitle}-content`}
          aria-expanded={expanded}
          id={`${ariaTitle}-toggle`}
        >
          <Row>
            <Col className={style.text}>
              <Typography variant="h5" component="span" className={style.title(disabled)}>
                {title}
              </Typography>
              {subtitle && !isActive && (hidden || furthestStep > stepIndex) && (
                <Typography component="span" className={style.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Col>
          </Row>
          {hidden && !disabled && (
            <Typography className={style.toggleActionText} component="span" variant="body2">
              EDIT
            </Typography>
          )}
        </button>
        <div className={style.content} aria-hidden={!expanded} id={`${ariaTitle}-content`}>
          {!disabled && !hidden && children}
        </div>
      </section>
    );
  }
);

AccordionStep.displayName = 'AccordionStep';

export default AccordionStep;
